import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './delivery-options-form.scss';
import { RadioGroup, Radio } from '@ampol/design-system';
import AmpolCashAddressForm from '../ampol-cash-address-form/ampol-cash-address-form';
import CartItemSummary from '../cart-item-summary/cart-item-summary';

/**
 * Deliver options radio group to select delivery option
 * @param cashOrderCartItems The key/value pair object containing cart items
 * @param cashOrderDefaults Default cash order values for account
 * @param selectedDeliveryOption The selected delivery option
 * @param formId Form unique id
 * @param singleShippingAddress Details for single shipping address
 * @param onSubmit AmpolCashAddressForm submit event
 * @param onStateChange AmpolCashAddressForm state change event
 * @param formType AmpolCashAddressForm 'shipping' or 'digital' form
 * @param addRecipient parent event
 * @param editRecipient parent event
 * @param removeRecipient parent event
 * @param onChange parent event
 *
 * @returns JSX Element
 */
const DeliveryOptionsForm = ({ cashOrderCartItems, cashOrderDefaults, selectedDeliveryOption, formId, singleShippingAddress, onSubmit,
  recipientsAddClick, recipientsEditClick, recipientsRemoveClick, onChange }) => {
  const physicalAddressRecipient = useSelector((state) => state.cashStore.cashOrderPhysicalAddressRecipient);
  const isDigitalCart = useSelector((state) => state.cashStore.isDigitalCartOnly);

  const [option, setOption] = useState(selectedDeliveryOption ? selectedDeliveryOption : 'optionOne');
  const [isAddressFormValid, setIsAddressFormValid] = useState(false);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    onChange && onChange(option, isValid);
  }, [option, isValid]);

  useEffect(() => {
    if (selectedDeliveryOption) {
      validateForm(selectedDeliveryOption);
    }
  }, [selectedDeliveryOption, physicalAddressRecipient, cashOrderCartItems]);

  useEffect(() => {
    setOption('optionOne');
    validateForm('optionOne');
  }, [isDigitalCart]);

  const validateForm = (option) => {
    if (option === 'optionOne') {
      // valid if it is not a digital cart with no defaulted email
      setIsValid(!(isDigitalCart && !cashOrderDefaults?.address?.email));
      return;
    }

    if (option === 'optionTwo') {
      setIsValid(isAddressFormValid);
      return;
    }

    if (option === 'optionThree') {
      let isOptionThreeValid = true;

      Object.keys(cashOrderCartItems).every((key) => {
        if (cashOrderCartItems[key].mediaType === 'D') {
          if (cashOrderCartItems[key].remainingRecipients > 0) {
            isOptionThreeValid = false;
            return false;
          }
          return true;
        } else {
          // for physical card validate that physical address has been set
          if (!physicalAddressRecipient) {
            isOptionThreeValid = false;
            return false;
          }
          return true;
        }
      });

      setIsValid(isOptionThreeValid);
      return;
    }
  };

  const onRadioChange = (value) => {
    if (value === 'optionOne' || value === 'optionTwo' || value === 'optionThree') {
      setOption(value);
      validateForm(value);
    }
  };

  const onStateChange = (state) => {
    setIsAddressFormValid(state.isValid);
  };

  return (
    <>
      <div className="delivery">
        <div className="delivery__title">Enter your shipping address details</div>
        <RadioGroup
          color="black"
          name={'options'}
          onChange={onRadioChange}
          direction={'column'}
          value={option}
          className={'delivery__items'}
        >
          <Radio label="Send card(s) to billing address" value="optionOne" />
          {option === 'optionOne' && (
            <div className={'delivery__optionOne'}>
              {isDigitalCart ? (
                <div className={'delivery__email'}>Email: {cashOrderDefaults?.address?.email}</div>
              ) : (
                <div>
                  Address: {[
                    cashOrderDefaults?.address?.line1,
                    cashOrderDefaults?.address?.line2,
                    cashOrderDefaults?.address?.city,
                    cashOrderDefaults?.address?.stateCode,
                    cashOrderDefaults?.address?.postCode
                  ].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          )}

          <Radio label={`Send card(s) to one ${isDigitalCart ? 'email' : 'shipping address'}`} value="optionTwo" />
          {option === 'optionTwo' && (
            <div className={'delivery__optionTwo'}>
              <div className={'delivery__address-tooltip'}>
                {isDigitalCart ? 'All digital cards will be sent to the email provided.' : 'All physical cards will be sent to the shipping address provided.'}
              </div>
              <AmpolCashAddressForm
                formId={formId}
                shippingAddress={singleShippingAddress}
                onSubmit={onSubmit}
                onStateChange={onStateChange}
                formType={isDigitalCart ? 'digital' : 'shipping'}
              />
            </div>
          )}
          {isDigitalCart && <Radio label="Send card(s) to multiple emails" value="optionThree" />}
          {option === 'optionThree' && isDigitalCart && (
            <div className={'delivery__optionThree'}>
              <ul aria-label="cart items" className="delivery__cart-list">
                {Object.keys(cashOrderCartItems).map((uuid) => (
                  <li key={uuid}>
                    <CartItemSummary
                      cartItem={cashOrderCartItems[uuid]}
                      showImage={true}
                      showRecipients={true}
                      recipientsAddClick={recipientsAddClick}
                      recipientsEditClick={recipientsEditClick}
                      recipientsRemoveClick={recipientsRemoveClick}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </RadioGroup>
      </div>
    </>
  );
};

export default DeliveryOptionsForm;
